import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row, Spinner } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { SOURCES, isPersonalized, readError } from "../../helpers/whatsappTemplates";
import { WhatsAppPreview } from "./WhatsAppPreview";

const chip = (on) => ({ display: "inline-flex", border: "1px solid " + (on ? "#111" : "#ddd"), borderRadius: 999, padding: "5px 11px", fontSize: 13,
    margin: "0 6px 6px 0", background: on ? "#111" : "#fff", color: on ? "#fff" : "#111", cursor: "pointer" });
const rule = { display: "flex", gap: 10, alignItems: "center", padding: "9px 12px", border: "1px solid #eee", borderRadius: 10, marginBottom: 6, background: "#fff", fontSize: 13.5 };
const num = { width: 22, height: 22, borderRadius: "50%", background: "#111", color: "#fff", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, flex: "none" };

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (iso) => { const d = iso ? new Date(iso) : new Date(Date.now() + 24 * 3600e3); return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` }; };
const TEST_KEY = "waCampaignTestNumber";

// Formulaire de campagne WhatsApp : contenu, audience (comptage en direct), produit, envoi.
export function CampaignForm() {
    const { id } = useParams();
    const history = useHistory();
    const isNew = id === "new";
    const [c, setC] = useState(null);
    const [when, setWhen] = useState(toLocal());
    const [templates, setTemplates] = useState([]);
    const [count, setCount] = useState(null);
    const [counting, setCounting] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [testNumber, setTestNumber] = useState(() => { try { return localStorage.getItem(TEST_KEY) || ""; } catch { return ""; } });
    const timer = useRef(null);
    const poll = useRef(null);
    const [testState, setTestState] = useState(null);
    useEffect(() => () => clearTimeout(poll.current), []);

    useEffect(() => {
        apiFetch(API.back + "/whatsapp-templates", { market: false }).then((r) => (r.ok ? r.json() : [])).then((l) => setTemplates(l.filter((t) => !t.system))).catch(() => {});
        if (isNew) { setC({ name: "", templateName: "", audience: { markets: ["fr", "es"], gender: "all", linkedOnly: false }, forcedProductSlug: "" }); return; }
        apiFetch(API.back + "/whatsapp-campaigns", { market: false })
            .then(async (res) => { if (!res.ok) throw new Error(await readError(res)); return res.json(); })
            .then((list) => { const found = list.find((x) => x.id === id); if (!found) throw new Error("campagne introuvable"); setC(found); setWhen(toLocal(found.scheduledAt)); })
            .catch((e) => setError(e.message));
    }, [id, isNew]);

    const scheduledAt = () => new Date(`${when.date}T${when.time}:00`).toISOString();
    const payload = () => ({ ...c, scheduledAt: scheduledAt() });

    // comptage en direct (anti-rebond 400 ms)
    useEffect(() => {
        if (!c || !c.templateName) { setCount(null); return; }
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setCounting(true);
            const res = await apiFetch(API.back + "/whatsapp-campaign-audience", { method: "POST", market: false, body: JSON.stringify(payload()) });
            setCounting(false);
            if (!res.ok) { setCount({ error: await readError(res) }); return; }
            setCount(await res.json());
        }, 400);
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [c && c.templateName, c && JSON.stringify(c.audience), c && c.forcedProductSlug]);

    if (error && !c) return <Alert color="danger">{error}</Alert>;
    if (!c) return <Spinner size="sm" />;

    const editable = isNew || c.status === "draft" || c.status === "scheduled";
    const set = (patch) => setC((x) => ({ ...x, ...patch }));
    const setAud = (patch) => set({ audience: { ...c.audience, ...patch } });
    const toggleMarket = (m) => setAud({ markets: c.audience.markets.includes(m) ? c.audience.markets.filter((x) => x !== m) : [...c.audience.markets, m] });
    const names = [...new Set(templates.map((t) => t.name))];
    const langs = (n) => templates.filter((t) => t.name === n).map((t) => `${t.language} ${t.status === "approved" ? "✓" : "…"}`).join(" / ");
    const tpl = templates.find((t) => t.name === c.templateName && t.status === "approved") || templates.find((t) => t.name === c.templateName);
    const personalized = isPersonalized(tpl);

    const save = async () => {
        setBusy(true); setError(null); setInfo(null);
        const res = await apiFetch(API.back + "/whatsapp-campaign", { method: "PUT", market: false, body: JSON.stringify(payload()) });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return null; }
        const saved = await res.json();
        setC(saved);
        if (isNew) history.replace(`/whatsapp/campaign/${saved.id}`);
        return saved;
    };
    const schedule = async () => {
        const saved = await save();
        if (!saved) return;
        if (!window.confirm(`Programmer l'envoi le ${when.date} à ${when.time} ?`)) return;
        const res = await apiFetch(API.back + "/whatsapp-campaign-action", { method: "POST", market: false, body: JSON.stringify({ id: saved.id, action: "schedule" }) });
        if (!res.ok) { setError(await readError(res)); return; }
        history.push("/whatsapp/campaigns");
    };
    const sendTest = async () => {
        const saved = await save();
        if (!saved) return;
        try { localStorage.setItem(TEST_KEY, testNumber); } catch { /* navigation privée */ }
        setBusy(true); setTestState(null);
        const res = await apiFetch(API.back + `/whatsapp-campaign-test?id=${saved.id}&to=${encodeURIComponent(testNumber.replace(/\D/g, ""))}`, { method: "POST", market: false });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return; }
        const { wamid } = await res.json().catch(() => ({}));
        followTest(wamid, testNumber);
    };
    // Meta accepte le message puis peut le refuser quelques secondes après : statut relu 30 s (toutes les 3 s).
    const followTest = (wamid, to) => {
        clearTimeout(poll.current);
        if (!wamid) { setTestState({ color: "success", text: `Test accepté par Meta pour ${to}.` }); return; }
        setTestState({ color: "info", text: `Test accepté par Meta pour ${to} — remise en cours…` });
        const started = Date.now();
        const tick = async () => {
            const r = await apiFetch(API.back + `/whatsapp-campaign-test?wamid=${encodeURIComponent(wamid)}`, { market: false }).catch(() => null);
            const st = r && r.ok ? await r.json().catch(() => null) : null;
            const status = st ? st.status : "pending";
            if (status === "failed") { setTestState({ color: "danger", text: `Test non remis à ${to} : ${st.reason || "échec Meta"}` }); return; }
            if (status === "read") { setTestState({ color: "success", text: `Test lu sur ${to} ✓✓` }); return; }
            const late = Date.now() - started >= 30000;
            if (status === "delivered") {
                setTestState({ color: "success", text: `Test remis sur ${to} ✓` });
                if (late) return;
            } else if (late) {
                setTestState({ color: "warning", text: `Test accepté par Meta pour ${to}, mais aucune confirmation de remise après 30 s (téléphone éteint ou hors ligne ?).` });
                return;
            }
            poll.current = setTimeout(tick, 3000);
        };
        poll.current = setTimeout(tick, 2000);
    };

    const sample = count && count.sample;
    const sourceTotal = count && count.sources ? Object.values(count.sources).reduce((a, b) => a + b, 0) : 0;
    return (
        <Container fluid className="p-0">
            <h1 className="h3 mb-3">{isNew ? "Nouvelle campagne" : c.name}</h1>
            {error && <Alert color="danger">{error}</Alert>}
            {info && <Alert color="success">{info}</Alert>}
            {testState && <Alert color={testState.color}>{testState.text}</Alert>}
            {!editable && <Alert color="info">Campagne {c.status} : plus modifiable. <a href={`/whatsapp/campaign/${c.id}/results`}>Voir les résultats</a>.</Alert>}
            <Row>
                <Col lg="8">
                    <Card><CardBody>
                        <h5>1. Contenu</h5>
                        <Row>
                            <Col md="6"><FormGroup><Label>Nom</Label><Input value={c.name} disabled={!editable} onChange={(e) => set({ name: e.target.value })} placeholder="Black Friday — vague 1" /></FormGroup></Col>
                            <Col md="6"><FormGroup><Label>Template</Label>
                                <Input type="select" value={c.templateName} disabled={!editable} onChange={(e) => set({ templateName: e.target.value })}>
                                    <option value="">— choisir —</option>
                                    {names.map((n) => <option key={n} value={n}>{n} · {langs(n)}</option>)}
                                </Input></FormGroup></Col>
                        </Row>
                    </CardBody></Card>
                    <Card><CardBody>
                        <h5>2. Audience</h5>
                        <div className="small text-muted mb-2">Contacts ayant accepté les bons plans, hors désinscrits et injoignables</div>
                        <Label className="small mb-1">Marché</Label>
                        <div>{[["fr", "France"], ["es", "Espagne"]].map(([m, l]) => <span key={m} style={chip(c.audience.markets.includes(m))} onClick={() => editable && toggleMarket(m)}>{l}</span>)}</div>
                        <Label className="small mb-1 mt-2">Genre du profil</Label>
                        <div>{[["all", "Tous"], ["Men", "Homme"], ["Women", "Femme"]].map(([g, l]) => <span key={g} style={chip(c.audience.gender === g)} onClick={() => editable && setAud({ gender: g })}>{l}</span>)}</div>
                        <Label className="small mb-1 mt-2">Compte TRC</Label>
                        <div>{[[false, "Tous"], [true, "Reliés seulement"]].map(([v, l]) => <span key={l} style={chip(c.audience.linkedOnly === v)} onClick={() => editable && setAud({ linkedOnly: v })}>{l}</span>)}</div>
                        <div className="small text-muted mt-2">Plafond : 2 campagnes par contact sur 30 jours</div>
                    </CardBody></Card>
                    {personalized && <Card><CardBody>
                        <h5>3. Produit proposé à chaque contact</h5>
                        <div className="small text-muted mb-2">Premier produit trouvé, en promo (−15 % ou plus) et disponible dans sa pointure</div>
                        <div style={rule}><span style={num}>1</span>Ses alertes ouvertes (prix ou taille)</div>
                        <div style={rule}><span style={num}>2</span>Ses favoris (compte relié)</div>
                        <div style={rule}><span style={num}>3</span>Les fiches d'où il vient (site, alertes)</div>
                        <div style={{ ...rule, background: "#fafafa" }}><span style={num}>4</span><div style={{ flex: 1 }}><b>Repli :</b> meilleure promo du jour dans son genre et sa pointure
                            <Input bsSize="sm" className="mt-2" placeholder="Forcer un produit (slug de la fiche, facultatif)" value={c.forcedProductSlug || ""} disabled={!editable}
                                onChange={(e) => set({ forcedProductSlug: e.target.value.trim() })} /></div></div>
                    </CardBody></Card>}
                    <Card><CardBody>
                        <h5>{personalized ? "4" : "3"}. Envoi</h5>
                        <Row className="align-items-end">
                            <Col md="3"><FormGroup><Label>Date</Label><Input type="date" value={when.date} disabled={!editable} onChange={(e) => setWhen({ ...when, date: e.target.value })} /></FormGroup></Col>
                            <Col md="2"><FormGroup><Label>Heure</Label><Input type="time" value={when.time} disabled={!editable} onChange={(e) => setWhen({ ...when, time: e.target.value })} /></FormGroup></Col>
                            <Col md="3"><FormGroup><Label>Numéro de test</Label><Input placeholder="33612345678" value={testNumber} onChange={(e) => setTestNumber(e.target.value)} /></FormGroup></Col>
                            <Col md="4" className="text-right"><FormGroup>
                                <Button color="light" className="mr-2" disabled={busy || !testNumber || !c.templateName} onClick={sendTest}>Envoyer un test</Button>
                                {editable && <Button color="light" className="mr-2" disabled={busy} onClick={save}>Enregistrer</Button>}
                                {editable && c.status !== "scheduled" && <Button color="primary" disabled={busy} onClick={schedule}>Programmer</Button>}
                            </FormGroup></Col>
                        </Row>
                    </CardBody></Card>
                </Col>
                <Col lg="4">
                    <div style={{ background: "#111", color: "#fff", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
                        <div className="small" style={{ opacity: 0.7 }}>Destinataires {counting && <Spinner size="sm" />}</div>
                        {count && count.error ? <div className="small mt-1">{count.error}</div> : <>
                            <div style={{ fontSize: 28, fontWeight: 800 }}>{count ? count.targeted : "—"}</div>
                            {count && <div className="small" style={{ opacity: 0.8 }}>{count.overCap} au plafond · {count.noTemplate} sans template dans leur langue{personalized ? ` · ${count.noProduct} sans produit en promo` : ""}</div>}
                            {personalized && sourceTotal > 0 && <div className="small mt-2">{Object.entries(count.sources).map(([k, v]) => `${(SOURCES[k] || k).split(" (")[0]} ${Math.round((100 * v) / sourceTotal)} %`).join(" · ")}</div>}
                        </>}
                    </div>
                    {sample && <Card><CardBody>
                        <div className="small text-muted mb-2">Aperçu pour un contact</div>
                        <WhatsAppPreview header={tpl && tpl.header} headerText={tpl && tpl.headerText} imageURL={sample.imageUrl || (tpl && tpl.headerImageUrl)} body={sample.body} footer={tpl && tpl.footer} buttons={(tpl && tpl.buttons) || []} />
                        {sample.source && <div className="small text-muted text-center">Produit choisi d'après : {SOURCES[sample.source]}</div>}
                    </CardBody></Card>}
                </Col>
            </Row>
        </Container>
    );
}
